import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { ServicePlan } from 'src/features/servicePlans/utils/types';
import { useUpdateServicePlan } from 'src/features/servicePlans/api/useUpdateServicePlan';
import { LIST_SERVICE_PLANS_RESOURCE } from 'src/features/servicePlans/api/useListServicePlans';
import ServicePlanFormModal from 'src/features/servicePlans/components/forms/ServicePlanFormModal';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { SERVICE_PLAN_RESOURCE } from 'src/features/servicePlans/api/useGetServicePlan';

interface UpdateServicePlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlan: ServicePlan;
    children?: React.ReactNode;
}

function UpdateServicePlanFormModal<TData>({
                                                   className,
                                                   semantics,
                                                   children,
                                                   selectedServicePlan,
                                               }: UpdateServicePlanFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateServicePlan({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLANS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [SERVICE_PLAN_RESOURCE] });
            toast({
                title: formatMessage(servicePlansMessages.updateServicePlanSuccess),
                description: formatMessage(servicePlansMessages.updateServicePlanSuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <ServicePlanFormModal
            selectedServicePlan={selectedServicePlan}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, id: selectedServicePlan.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(servicePlansMessages.editServicePlanSubtitle, {
                entity: selectedServicePlan.name,
            })}
        >
            {children}
        </ServicePlanFormModal>
    );
}

export default UpdateServicePlanFormModal;
